/**
 * Centralized badge/status color utilities
 * Replaces duplicated getStatusColor/getStageColor functions across modules
 *
 * Usage:
 * ```tsx
 * import { getStatusColor, getStageColor, getPriorityColor } from '@/client/lib/badge-styles'
 *
 * <Badge className={getStatusColor('active', 'contact')}>Active</Badge>
 * <Badge className={getStageColor('qualified')}>Qualified</Badge>
 * ```
 */

// =============================================================================
// Generic Status Colors (contacts, companies, products)
// =============================================================================

export type EntityStatus = 'active' | 'inactive' | 'archived' | 'lead' | 'customer' | 'prospect'

const STATUS_COLORS: Record<EntityStatus, string> = {
  active: 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20',
  inactive: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20',
  archived: 'bg-gray-500/10 text-gray-700 dark:text-gray-400 border-gray-500/20',
  lead: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20',
  customer: 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20',
  prospect: 'bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/20',
}

/**
 * Get color classes for entity status badges (contacts, companies, products)
 */
export function getStatusColor(status: string): string {
  return STATUS_COLORS[status as EntityStatus] ?? STATUS_COLORS.archived
}

// =============================================================================
// Deal Stage Colors
// =============================================================================

export type DealStage =
  | 'lead'
  | 'qualified'
  | 'proposal'
  | 'negotiation'
  | 'closed_won'
  | 'closed_lost'

const STAGE_COLORS: Record<DealStage, string> = {
  lead: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  qualified: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  proposal: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
  negotiation: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
  closed_won: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  closed_lost: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
}

const STAGE_LABELS: Record<DealStage, string> = {
  lead: 'Lead',
  qualified: 'Qualified',
  proposal: 'Proposal',
  negotiation: 'Negotiation',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
}

/**
 * Get color classes for deal stage badges
 */
export function getStageColor(stage: string): string {
  return STAGE_COLORS[stage as DealStage] ?? STAGE_COLORS.lead
}

/**
 * Get human-readable label for deal stage
 */
export function getStageLabel(stage: string): string {
  return STAGE_LABELS[stage as DealStage] ?? stage
}

// =============================================================================
// Priority Colors (tasks)
// =============================================================================

export type Priority = 'urgent' | 'high' | 'medium' | 'low'

const PRIORITY_COLORS: Record<Priority, string> = {
  urgent: 'bg-destructive text-destructive-foreground',
  high: 'bg-orange-500/20 text-orange-700 dark:text-orange-400 border-orange-500/30',
  medium: 'bg-accent text-accent-foreground',
  low: 'bg-secondary text-secondary-foreground',
}

const PRIORITY_DOT_COLORS: Record<Priority, string> = {
  urgent: 'bg-destructive',
  high: 'bg-orange-500',
  medium: 'bg-accent-foreground/50',
  low: 'bg-muted-foreground/30',
}

/**
 * Get color classes for priority badges
 */
export function getPriorityColor(priority: string): string {
  return PRIORITY_COLORS[priority as Priority] ?? PRIORITY_COLORS.medium
}

/**
 * Get color class for priority dot indicators
 */
export function getPriorityDotColor(priority: string): string {
  return PRIORITY_DOT_COLORS[priority as Priority] ?? PRIORITY_DOT_COLORS.medium
}

// =============================================================================
// Task Status Colors
// =============================================================================

export type TaskStatus = 'open' | 'in_progress' | 'completed' | 'cancelled'

const TASK_STATUS_COLORS: Record<TaskStatus, string> = {
  open: '', // No special styling for open
  in_progress: 'bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-500/30',
  completed: 'bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/30',
  cancelled: 'bg-muted text-muted-foreground',
}

const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

/**
 * Get color classes for task status badges
 */
export function getTaskStatusColor(status: string): string {
  return TASK_STATUS_COLORS[status as TaskStatus] ?? ''
}

/**
 * Get human-readable label for task status
 */
export function getTaskStatusLabel(status: string): string {
  return TASK_STATUS_LABELS[status as TaskStatus] ?? status
}

// =============================================================================
// Event Colors
// =============================================================================

export type EventType = 'meeting' | 'call' | 'task' | 'deadline' | 'reminder' | 'other'
export type EventStatus = 'scheduled' | 'completed' | 'cancelled'

const EVENT_TYPE_COLORS: Record<EventType, string> = {
  meeting: 'bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-500/30',
  call: 'bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/30',
  task: 'bg-purple-500/20 text-purple-700 dark:text-purple-400 border-purple-500/30',
  deadline: 'bg-red-500/20 text-red-700 dark:text-red-400 border-red-500/30',
  reminder: 'bg-orange-500/20 text-orange-700 dark:text-orange-400 border-orange-500/30',
  other: 'bg-gray-500/20 text-gray-700 dark:text-gray-400 border-gray-500/30',
}

const EVENT_STATUS_COLORS: Record<EventStatus, string> = {
  scheduled: 'bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-500/30',
  completed: 'bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/30',
  cancelled: 'bg-muted text-muted-foreground',
}

/**
 * Get color classes for event type badges
 */
export function getEventTypeColor(type: string): string {
  return EVENT_TYPE_COLORS[type as EventType] ?? EVENT_TYPE_COLORS.other
}

/**
 * Get color classes for event status badges
 */
export function getEventStatusColor(status: string): string {
  return EVENT_STATUS_COLORS[status as EventStatus] ?? EVENT_STATUS_COLORS.scheduled
}

// =============================================================================
// Common Date Badge Colors
// =============================================================================

/**
 * Get color classes for overdue dates
 */
export function getOverdueDateColor(): string {
  return 'bg-destructive/20 text-destructive border-destructive/30'
}

/**
 * Get color classes for due-today dates
 */
export function getDueTodayColor(): string {
  return 'bg-orange-500/20 text-orange-700 dark:text-orange-400 border-orange-500/30'
}

/**
 * Get color classes for source badges (AI, auto-generated, etc.)
 */
export function getSourceColor(source: string): string {
  switch (source) {
    case 'ai':
    case 'auto':
      return 'bg-purple-500/20 text-purple-700 dark:text-purple-400 border-purple-500/30'
    default:
      return ''
  }
}
